/*----------------------------------
- GENERATED FILE
----------------------------------*/

// This file is generated by Proteum from client/pages/marketing/api-docs.tsx.
// Do not edit it manually.

import type __GeneratedRouteApp from "@/client/index";

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';
import { ArrowRight, BookOpen, Code, Database, FileText, Key, Shield, Webhook, Zap } from 'lucide-preact';

/*----------------------------------
- APP
----------------------------------*/

import Motion, { fadeIn } from '@/client/components/Motion';
import PublicFooter from '@/client/components/marketing/PublicFooter';
import PublicHeader from '@/client/components/marketing/PublicHeader';
import Page from '@/client/pages/_layout/_page';
import { buildContactHref } from '@/client/catalogs/marketing/navigation';

/*----------------------------------
- CONTROLEUR
----------------------------------*/

const API_DOCS_CONTACT_HREF = buildContactHref({
  topic: 'api-access',
  source: 'api-docs'
});

export const __register = (app: __GeneratedRouteApp) => {
  const { Router } = app;
  return Router.page('/api-docs', { ...({
  static: {
    refresh: '1h',
    urls: ['/api-docs']
  }
}), ...{ filepath: "/app/client/pages/marketing/api-docs.tsx", sourceLocation: { line: 28, column: 1 }, id: "marketing_api_docs" } }, null, ({}) => <Page withLayout={false}>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
            <PublicHeader ctaLabel="Get API Access" ctaHref={API_DOCS_CONTACT_HREF} />

            <section className="pt-32 pb-20 px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    <Motion tag="div" initial="hidden" animate="visible" variants={fadeIn} className="text-center">
                        <div className="inline-flex items-center gap-2 bg-slate-200 text-slate-700 px-4 py-2 rounded-full mb-6 font-semibold">
                            <Code size={18} />
                            API Documentation
                        </div>
                        <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
                            Integrate CrossPath
                        </h1>
                        <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                            Build powerful integrations with our API. Access candidate data, manage recruitment missions, and automate workflows with developer-friendly endpoints.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href={API_DOCS_CONTACT_HREF} className="bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-black text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center justify-center gap-2">
                                Get API Access
                                <ArrowRight size={20} />
                            </a>
                            <a href="https://docs.crosspath.com" target="_blank" rel="noreferrer" className="border-2 border-slate-700 text-slate-700 hover:bg-slate-50 px-8 py-4 rounded-xl font-semibold text-lg transition-all inline-flex items-center justify-center gap-2">
                                <BookOpen size={20} />
                                View Full Docs
                            </a>
                        </div>
                    </Motion>
                </div>
            </section>

            <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-white to-slate-50">
                <div className="max-w-6xl mx-auto">
                    <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-slate-900 mb-4">API Features</h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto">Everything you need to build seamless integrations.</p>
                    </Motion>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {[{
            icon: Zap,
            title: 'RESTful API',
            description: 'Clean, intuitive endpoints with consistent response formats.'
          }, {
            icon: Shield,
            title: 'Secure Authentication',
            description: 'OAuth 2.0 and API key authentication with fine-grained controls.'
          }, {
            icon: Database,
            title: 'Real-time Data',
            description: 'Access candidate profiles, job postings, and application statuses in real time.'
          }, {
            icon: Webhook,
            title: 'Webhooks',
            description: 'Receive event notifications for new applications and status changes.'
          }].map((feature, index) => <Motion tag="div" key={feature.title} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-2xl transition-all">
                                <div className="w-14 h-14 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl flex items-center justify-center mb-4">
                                    <feature.icon className="w-7 h-7 text-white" />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                            </Motion>)}
                    </div>
                </div>
            </section>

            <section className="py-20 px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-slate-900 mb-4">Integration Use Cases</h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto">What you can build with our API.</p>
                    </Motion>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {[{
            icon: Database,
            title: 'ATS Integration',
            description: 'Sync candidates and job postings with your applicant tracking system.'
          }, {
            icon: FileText,
            title: 'Custom Dashboards',
            description: 'Build bespoke analytics and reporting experiences on top of recruitment data.'
          }, {
            icon: Zap,
            title: 'Workflow Automation',
            description: 'Connect CrossPath with Zapier, Make, or internal automation scripts.'
          }, {
            icon: BookOpen,
            title: 'Client Portals',
            description: 'Create white-label client portals powered by CrossPath data.'
          }].map((useCase, index) => <Motion tag="div" key={useCase.title} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            delay: index * 0.1
          }} className="bg-gradient-to-br from-white to-slate-50 rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-2xl transition-all">
                                <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                                    <useCase.icon className="w-7 h-7 text-slate-700" />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-3">{useCase.title}</h3>
                                <p className="text-slate-600 leading-relaxed">{useCase.description}</p>
                            </Motion>)}
                    </div>
                </div>
            </section>

            <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-white">
                <div className="max-w-6xl mx-auto">
                    <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-slate-900 mb-4">API Endpoints</h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto">Preview of the API surface.</p>
                    </Motion>

                    <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="bg-slate-900 rounded-2xl p-8 shadow-2xl overflow-hidden">
                        <div className="space-y-4">
                            {[{
              method: 'GET',
              path: '/api/v1/candidates',
              description: 'List candidates with filtering and pagination.'
            }, {
              method: 'POST',
              path: '/api/v1/candidates',
              description: 'Create a new candidate profile.'
            }, {
              method: 'GET',
              path: '/api/v1/missions',
              description: 'Retrieve recruitment missions and job postings.'
            }, {
              method: 'POST',
              path: '/api/v1/missions',
              description: 'Create a new recruitment mission.'
            }, {
              method: 'GET',
              path: '/api/v1/applications',
              description: 'List applications with status tracking.'
            }, {
              method: 'PATCH',
              path: '/api/v1/applications/:id',
              description: 'Update application status and notes.'
            }].map((endpoint, index) => <Motion tag="div" key={`${endpoint.method}:${endpoint.path}`} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-slate-800 rounded-lg p-4 border border-slate-700 hover:border-slate-500 transition-all">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                        <span className={`px-3 py-1 rounded-md text-xs font-bold ${endpoint.method === 'GET' ? 'bg-blue-500/20 text-blue-400' : endpoint.method === 'POST' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                            {endpoint.method}
                                        </span>
                                        <code className="text-slate-300 font-mono text-sm">{endpoint.path}</code>
                                    </div>
                                    <p className="text-slate-400 text-sm mt-2 ml-0 sm:ml-16">{endpoint.description}</p>
                                </Motion>)}
                        </div>
                    </Motion>
                </div>
            </section>

            <section className="py-20 px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-slate-900 mb-4">Quick Start Guide</h2>
                        <p className="text-xl text-slate-600">Get up and running in minutes.</p>
                    </Motion>

                    <div className="space-y-4">
                        {['Create a CrossPath account and navigate to Settings → API Keys.', 'Generate your API key and securely store it.', 'Make your first API call using the authentication header.', 'Explore the interactive documentation for detailed endpoints.', 'Join the developer community for support and best practices.'].map((step, index) => <Motion tag="div" key={index} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 flex items-start gap-4">
                                <div className="w-10 h-10 bg-gradient-to-br from-slate-700 to-slate-900 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                                    {index + 1}
                                </div>
                                <p className="text-slate-700 text-lg pt-1">{step}</p>
                            </Motion>)}
                    </div>
                </div>
            </section>

            <section className="py-20 px-6 lg:px-8 bg-gradient-to-r from-slate-700 to-slate-900">
                <div className="max-w-4xl mx-auto text-center">
                    <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn}>
                        <Key className="w-16 h-16 text-white mx-auto mb-6" />
                        <h2 className="text-4xl font-bold text-white mb-6">Ready to Integrate?</h2>
                        <p className="text-xl text-white/90 mb-8 leading-relaxed">
                            Get your API keys and start building powerful recruitment integrations today.
                        </p>
                        <a href={API_DOCS_CONTACT_HREF} className="bg-white text-slate-900 hover:bg-slate-50 px-8 py-4 rounded-xl font-semibold text-lg transition-all shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center gap-2">
                            Get API Access
                            <ArrowRight size={20} />
                        </a>
                        <p className="text-white/70 text-sm mt-6">Free tier available. No credit card required.</p>
                    </Motion>
                </div>
            </section>

            <PublicFooter tagline="Integrate candidate, mission, and workflow data with a CrossPath API layer designed for internal tools and partner products." />
        </div>
    </Page>);
};
